/* eslint-disable no-unused-vars */
import AssessmentIcon from '@mui/icons-material/Assessment';
import BusinessIcon from '@mui/icons-material/Business';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import GroupIcon from '@mui/icons-material/Group';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import VisibilityIcon from '@mui/icons-material/Visibility';
import WorkIcon from '@mui/icons-material/Work';
import {
    Avatar,
    Box,
    Chip,
    Paper,
    Stack,
    Typography,
    alpha,
    useTheme
} from '@mui/material';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';



const getPreviewUser = (userId, employeePreview) => {
    if (!userId || !Array.isArray(employeePreview)) return null;
    const found = employeePreview.find(u =>
        String(u.employeeId).toLowerCase() === String(userId).toLowerCase() ||
        (u.user && String(u.user.email).toLowerCase() === String(userId).toLowerCase())
    );
    return found;
};

const getFullName = (user) => {
    if (!user) return '';
    const initial = user.initial || '';
    const names = [user.firstname, user.middlename, user.lastname].filter(n => n && n.trim());
    const fullName = names.join(' ');
    return initial ? `${initial} ${fullName}` : fullName;
};

const InfoRow = ({ icon, label, value }) => (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
        <Box
            sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                color: 'primary.main',
                mr: 2,
                flexShrink: 0
            }}
        >
            {icon}
        </Box>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" display="block">
                {label}
            </Typography>
            <Typography variant="body2" fontWeight="medium" sx={{ wordBreak: 'break-word' }}>
                {value || 'Not specified'}
            </Typography>
        </Box>
    </Box>
);

const OrganizationalInfo = ({ kpiDetails, employeePreview = [] }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    
    const {
        user,
        designation,
        department,
        goals,
        rolesAtAssignment = [],
        directReportsAtAssignment = [],
        employeeInfo,
        designationAtAssignment,
        assignedByEmployee
    } = kpiDetails;

    // Check if any scores exist across all goals
    const hasAnyScores = () => {
        return goals.some(goal => goal.scores && goal.scores.length > 0);
    };

    // Use designationAtAssignment if available, otherwise fall back to designation
    const currentDesignation = designationAtAssignment?.name || designation;
    
    // Use employeeInfo.department if available, otherwise fall back to department
    const currentDepartment = employeeInfo?.department?.name || department;

    // Prefer preview data for user info if available
    let previewUser = null;
    if (user) {
        previewUser = getPreviewUser(user.employeeId, employeePreview)
            || getPreviewUser(user.email, employeePreview);
    }
    const displayUser = employeeInfo || previewUser || user || {};
    const fullName = getFullName(displayUser);

    // Get reporting manager info
    const reportingManagerEmail = employeeInfo?.reportingManager || assignedByEmployee;
    const reportingManagerUser = getPreviewUser(reportingManagerEmail, employeePreview);
    const reportingManagerName = reportingManagerUser ? getFullName(reportingManagerUser) : reportingManagerEmail;

    // Calculate overall performance score and feedback
    const calculateOverallScore = () => {
        const { goals = [] } = kpiDetails;
        if (!goals || goals.length === 0) return null;
        
        // Only count goals that have scores
        const goalsWithScores = goals.filter(goal => goal.scores && goal.scores.length > 0);
        if (goalsWithScores.length === 0) return null;
        
        let totalWeightedScore = 0;
        let totalWeight = 0;

        goalsWithScores.forEach(goal => {
            const average = goal.scores.reduce((acc, score) => acc + parseFloat(score.score || 0), 0) / goal.scores.length;
            const weight = parseFloat(goal.weight || 0);
            totalWeightedScore += average * weight;
            totalWeight += weight;
        });

        if (totalWeight === 0) return null;
        const raw = totalWeightedScore / totalWeight;
        const fractional = raw - Math.floor(raw);
        return fractional >= 0.9 ? Math.ceil(raw) : raw;
    };

    const overallScore = calculateOverallScore();
    
    const getPerformanceFeedback = (score) => {
        if (score === null) {
            return {
                range: 'N/A',
                title: 'No Evaluations',
                description: 'No scores have been assigned yet',
                criteria: 'Performance evaluation has not started. Once scores are assigned, feedback will be generated.',
                color: theme.palette.text.disabled
            };
        }
        
        if (score >= 0 && score < 1) {
            return {
                range: '0 - 1',
                title: 'Failing',
                description: 'Had opportunities but no action was taken',
                criteria: 'Employee was assigned a project deadline extension task but never started working on it despite multiple reminders.',
                color: theme.palette.error.main
            };
        } else if (score >= 1 && score < 2) {
            return {
                range: '1 - 2', 
                title: 'Requires Improvement',
                description: 'Started with no outcome',
                criteria: 'Employee began researching a new process but abandoned it halfway without any deliverables.',
                color: theme.palette.warning.dark
            };
        } else if (score >= 2 && score < 3) {
            return {
                range: '2 - 3',
                title: 'Something Achieving', 
                description: 'Did not provide feedback',
                criteria: 'Employee implemented a small process change that saved 5 minutes per task but didn\'t scale it.',
                color: theme.palette.warning.main
            };
        } else if (score >= 3 && score < 4) {
            return {
                range: '3 - 4',
                title: 'Achieving',
                description: 'Actions having little to no effect',
                criteria: 'Employee completed the assigned task but didn\'t communicate progress updates to the team.',
                color: theme.palette.info.main
            };
        } else {
            return {
                range: '4 - 5',
                title: 'Great',
                description: 'Able to implement',
                criteria: 'Employee successfully automated a manual process, reducing processing time by 30%.',
                color: theme.palette.success.dark
            };
        }
    };

    const performanceFeedback = getPerformanceFeedback(overallScore);

    return (
        <Stack spacing={3}>
            {/* Profile Card */}
            <Paper
                elevation={0}
                sx={{
                    p: 3,
                    borderRadius: 3,
                    bgcolor: 'background.paper',
                    border: `1px solid ${theme.palette.divider}`,
                    textAlign: 'center'
                }}
            >
                <Avatar
                    sx={{
                        width: 80,
                        height: 80,
                        margin: '0 auto 16px',
                        border: `4px solid ${theme.palette.mode === 'dark' ? alpha(theme.palette.primary.main, 0.3) : '#e3f2fd'}`,
                        fontSize: '2rem',
                        bgcolor: theme.palette.primary.main
                    }}
                    src={displayUser.user?.profilePicture || displayUser.avatar || null}
                >
                    {(() => {
                        const name = fullName || displayUser.name || displayUser.employeeId || '';
                        if (!displayUser.user?.profilePicture && !displayUser.avatar) {
                            const parts = name.split(' ').filter(Boolean);
                            if (parts.length === 1) return parts[0][0]?.toUpperCase() || 'U';
                            if (parts.length > 1) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
                            return 'U';
                        }
                        return null;
                    })()}
                </Avatar>

                <Typography variant="h6" fontWeight={600} sx={{ mb: 0.5, color: 'text.primary' }}>
                    {fullName || displayUser.name || displayUser.employeeId || 'Employee'}
                </Typography>

                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                    {displayUser.user?.email || displayUser.email || displayUser.employeeId || ''}
                </Typography>

                <Box display="flex" justifyContent="center" gap={1} flexWrap="wrap">
                    <Chip
                        label={displayUser.employeeId || 'N/A'}
                        size="small"
                        sx={{
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main,
                            fontWeight: 600,
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                        }}
                    />
                    {displayUser.user?.accountIsActive && (
                        <Chip
                            label="Active"
                            size="small"
                            sx={{
                                bgcolor: alpha(theme.palette.success.main, 0.1),
                                color: theme.palette.success.main,
                                fontWeight: 600,
                                border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`
                            }}
                        />
                    )}
                </Box>
            </Paper>

            {/* Employment Details */}
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, bgcolor: 'background.paper', border: `1px solid ${theme.palette.divider}` }}>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 3 }}>
                    Employment Details
                </Typography>

                <InfoRow
                    icon={<WorkIcon />}
                    label="Designation"
                    value={currentDesignation}
                />

                <Box sx={{ ml: 7, mb: 2 }}>
                    <Box
                        component="span"
                        sx={{
                            display: 'inline-block',
                            px: 1.5,
                            py: 0.5,
                            bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.warning.main, 0.15) : '#fff3cd',
                            color: theme.palette.mode === 'dark' ? theme.palette.warning.light : '#856404',
                            border: `1px solid ${theme.palette.mode === 'dark' ? alpha(theme.palette.warning.main, 0.3) : '#ffeaa7'}`,
                            borderRadius: 1,
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            textAlign: 'center'
                        }}
                    >
                        Designation shown is for the selected KPI period
                    </Box>
                </Box>

                <InfoRow
                    icon={<BusinessIcon />}
                    label="Department"
                    value={currentDepartment}
                />

                {displayUser.joiningDate && (
                    <InfoRow
                        icon={<CalendarTodayIcon />}
                        label="Joining Date"
                        value={dayjs(displayUser.joiningDate).format('MMM DD, YYYY')}
                    />
                )}
            </Paper>

            {/* Reporting Structure */}
            {reportingManagerEmail && (
                <Paper elevation={0} sx={{ p: 3, borderRadius: 3, bgcolor: 'background.paper', border: `1px solid ${theme.palette.divider}` }}>
                    <Typography variant="h6" fontWeight={600} sx={{ mb: 3 }}>
                        Reporting Structure
                    </Typography>

                    <InfoRow
                        icon={<SupervisorAccountIcon />}
                        label="Reports To"
                        value={reportingManagerName}
                    />

                    {directReportsAtAssignment && directReportsAtAssignment.length > 0 && (
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Box
                                    sx={{
                                        width: 40,
                                        height: 40,
                                        borderRadius: 2,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                                        color: 'primary.main',
                                        mr: 2
                                    }}
                                >
                                    <GroupIcon />
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        Assigned Employees
                                    </Typography>
                                    <Typography variant="body2" fontWeight="medium">
                                        {directReportsAtAssignment.length} member{directReportsAtAssignment.length !== 1 ? 's' : ''}
                                    </Typography>
                                </Box>
                            </Box>

                            <Box sx={{ pl: 7 }}>
                                <Stack spacing={1}>
                                    {directReportsAtAssignment.map((reportEmail, index) => {
                                        const reportUser = getPreviewUser(reportEmail, employeePreview);
                                        const reportName = reportUser ? getFullName(reportUser) : reportEmail;
                                        return (
                                            <Box
                                                key={index}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    p: 1,
                                                    borderRadius: 1,
                                                    bgcolor: alpha(theme.palette.divider, 0.06),
                                                    justifyContent: 'space-between'
                                                }}
                                            >
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <Avatar
                                                        sx={{ width: 28, height: 28, fontSize: '0.875rem' }}
                                                        src={reportUser?.user?.profilePicture || null}
                                                    >
                                                        {reportName[0]?.toUpperCase() || 'U'}
                                                    </Avatar>
                                                    <Typography variant="body2">
                                                        {reportName}
                                                    </Typography>
                                                </Box>
                                                <Box
                                                    component="span"
                                                    sx={{ 
                                                        cursor: 'pointer', 
                                                        color: 'primary.main', 
                                                        display: 'flex', 
                                                        alignItems: 'center',
                                                        p: 0.5
                                                    }}
                                                    onClick={() => {
                                                        const params = new URLSearchParams(window.location.search);
                                                        params.set('userId', reportEmail);
                                                        params.delete('kpiPeriod');
                                                        navigate(`${window.location.pathname}?${params.toString()}`);
                                                    }}
                                                >
                                                    <VisibilityIcon fontSize="small" />
                                                </Box>
                                            </Box>
                                        );
                                    })}
                                </Stack>
                            </Box>
                            <Box sx={{ mt: 2 }}>
                                <Box
                                    component="span"
                                    sx={{
                                        display: 'inline-block',
                                        px: 1.5,
                                        py: 0.5,
                                        bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.warning.main, 0.15) : '#fff3cd',
                                        color: theme.palette.mode === 'dark' ? theme.palette.warning.light : '#856404',
                                        border: `1px solid ${theme.palette.mode === 'dark' ? alpha(theme.palette.warning.main, 0.3) : '#ffeaa7'}`,
                                        borderRadius: 1,
                                        fontSize: '0.75rem',
                                        fontWeight: 500,
                                        textAlign: 'center'
                                    }}
                                >
                                    Reporting Structure shown is for the selected KPI period
                                </Box>
                            </Box>
                        </Box>
                    )}
                </Paper>
            )}

            {/* Performance Feedback */}
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, bgcolor: 'background.paper', border: `1px solid ${theme.palette.divider}` }}>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 3 }}>
                    Performance Feedback
                </Typography>

                {!hasAnyScores() ? (
                    <Box textAlign="center" py={6}>
                        <AssessmentIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary">
                            Scoring is not started
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            No scores have been assigned to any goals yet
                        </Typography>
                    </Box>
                ) : (
                    <>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3, mb: 3 }}>
                            <Box
                                sx={{
                                    width: 60,
                                    height: 60,
                                    borderRadius: 2,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    bgcolor: alpha(performanceFeedback.color, 0.1),
                                    color: performanceFeedback.color,
                                    flexShrink: 0
                                }}
                            >
                                <AssessmentIcon sx={{ fontSize: 32 }} />
                            </Box>
                            
                            <Box sx={{ flexGrow: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                                    <Typography variant="h6" fontWeight={600} color={performanceFeedback.color}>
                                        {performanceFeedback.title}
                                    </Typography>
                                    <Chip
                                        label={`${performanceFeedback.range} Range`}
                                        size="small"
                                        sx={{
                                            bgcolor: alpha(performanceFeedback.color, 0.1),
                                            color: performanceFeedback.color,
                                            fontWeight: 600
                                        }}
                                    />
                                </Box>
                                
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
                                    {performanceFeedback.description}
                                </Typography>
                                
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <Typography variant="body2" fontWeight={600}>
                                        Current Score:
                                    </Typography>
                                    {overallScore !== null ? (
                                        <Typography variant="body2" color={performanceFeedback.color} fontWeight={600}>
                                            {Number(overallScore).toFixed(2)}/5
                                        </Typography>
                                    ) : (
                                        <Typography variant="body2" color="text.disabled" fontWeight={600}>
                                            N/A
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        </Box>

                        {/* Performance Criteria Details */}
                        <Box sx={{ borderTop: `1px solid ${theme.palette.divider}`, pt: 3 }}>
                            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2, color: 'text.primary' }}>
                                Performance Criteria:
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                {performanceFeedback.criteria}
                            </Typography>

                            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2, color: 'text.primary' }}>
                                Score Range Breakdown:
                            </Typography>
                            
                            <Stack spacing={1.5}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, bgcolor: alpha(theme.palette.divider, 0.06), borderRadius: 1 }}>
                                    <Box>
                                        <Typography variant="body2" fontWeight={600} color={theme.palette.error.main}>0 - 1: Failing</Typography>
                                        <Typography variant="caption" color="text.secondary">Had opportunities but no action was taken</Typography>
                                    </Box>
                                </Box>
                                
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, bgcolor: alpha(theme.palette.divider, 0.06), borderRadius: 1 }}>
                                    <Box>
                                        <Typography variant="body2" fontWeight={600} color={theme.palette.warning.dark}>1 - 2: Requires Improvement</Typography>
                                        <Typography variant="caption" color="text.secondary">Started with no outcome</Typography>
                                    </Box>
                                </Box>
                                
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, bgcolor: alpha(theme.palette.divider, 0.06), borderRadius: 1 }}>
                                    <Box>
                                        <Typography variant="body2" fontWeight={600} color={theme.palette.warning.main}>2 - 3: Something Achieving</Typography>
                                        <Typography variant="caption" color="text.secondary">Did not provide feedback</Typography>
                                    </Box>
                                </Box>
                                
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, bgcolor: alpha(theme.palette.divider, 0.06), borderRadius: 1 }}>
                                    <Box>
                                        <Typography variant="body2" fontWeight={600} color={theme.palette.info.main}>3 - 4: Achieving</Typography>
                                        <Typography variant="caption" color="text.secondary">Actions having little to no effect</Typography>
                                    </Box>
                                </Box>
                                
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, bgcolor: alpha(theme.palette.divider, 0.06), borderRadius: 1 }}>
                                    <Box>
                                        <Typography variant="body2" fontWeight={600} color={theme.palette.success.dark}>4 - 5: Great</Typography>
                                        <Typography variant="caption" color="text.secondary">Able to implement</Typography>
                                    </Box>
                                </Box>
                            </Stack>
                </Box>
                    </>
                )}
            </Paper>
        </Stack>
    );
};

export default OrganizationalInfo;